import { useCallback, useLayoutEffect, useRef } from "react";
import { PLACEMENT_KEY_ATTRIBUTE } from "./boardAttributes";
import { playCardFadeIn } from "./boardEntrance";
import { startLanding, type BoardLanding } from "./boardLanding";
import type { BoardMotion } from "./boardMotion";

interface UseBoardLandingScrollArgs {
    motion: BoardMotion;
    /** The dragged card's key; null once the drop has settled. */
    activeKey: string | null;
    /** The dropped card's key once the move is applied. */
    focusKey: string | null;
}

/**
 * A pointer drop that lands outside its column's view: `land` (called from
 * the drop animation) scrolls that column to the card, and once the drop
 * settles the card plays the entrance fade-in where it landed, since the
 * overlay faded instead of travelling there. No other column moves.
 */
export const useBoardLandingScroll = ({
    motion,
    activeKey,
    focusKey,
}: UseBoardLandingScrollArgs) => {
    const unseenLandingRef = useRef<BoardLanding | null>(null);

    const land = useCallback(
        (node: HTMLElement) => {
            const landing = startLanding(node, motion);
            unseenLandingRef.current = landing?.wasOutOfView ? landing : null;
            return landing;
        },
        [motion],
    );

    // The landed card mounts on the commit that ends the drop, in the
    // scroller the landing moved.
    useLayoutEffect(() => {
        const landing = unseenLandingRef.current;
        if (activeKey !== null || landing === null || focusKey === null) {
            return;
        }
        unseenLandingRef.current = null;
        const slot = landing.scroller.querySelector(
            `[${PLACEMENT_KEY_ATTRIBUTE}="${CSS.escape(focusKey)}"]`,
        );
        if (slot instanceof HTMLElement && typeof slot.animate === "function") {
            playCardFadeIn(slot, motion);
        }
    }, [activeKey, focusKey, motion]);

    return land;
};
